/*
  Bubble button hover. Enhances pill-style CTAs with a circle that expands from
  the cursor to fill the button. Styling lives in assets/bubble-button.css;
  this script only builds the DOM and drives the circle position/scale.
  Drawer CTA is mobile-only and always enhanced; other buttons stay desktop-only.
*/
(() => {
  const hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');

  const BUBBLE_SIZE = 48;
  const DRAWER_CTA_SELECTOR = '.sc-drawer__cta';
  const SELECTOR = [
    '.button:not(.button-unstyled)',
    '.button-secondary',
    '.perform__cta',
    '.product-carousel__cta',
    '.partnership__cta',
    '.image-banner__cta',
    '.header-actions__cta',
    '.sc-footer__submit',
    '.news__card-cta',
    '.sc-menu-card__btn',
    '.sc-bundle-bar__button',
    '.sc-spec__cta',
    '.sc-button:not(.sc-button--placeholder)',
    '.sc-reseller-grid__view-more',
    '.sc-video-card__play',
    '.sc-download-card__button',
    '.sc-cfg__button',
  ].join(',');

  const VARIANT_CLASSES = [
    'bubble-button--primary',
    'bubble-button--outline',
    'bubble-button--dark',
    'bubble-button--light',
    'bubble-button--orange',
    'bubble-button--ink',
  ];

  const isBrandOrange = ({ r, g, b }) => r > 190 && g > 40 && g < 130 && b < 80;

  const parseRgb = (color) => {
    const m = color.match(/rgba?\((\d+)[,\s]+(\d+)[,\s]+(\d+)/);
    return m ? { r: +m[1], g: +m[2], b: +m[3] } : null;
  };

  const getColorVariant = (el) => {
    const rgb = parseRgb(getComputedStyle(el).backgroundColor);
    if (!rgb) return 'primary';
    if (isBrandOrange(rgb)) return 'primary';
    const brightness = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
    return brightness < 128 ? 'dark' : 'orange';
  };

  const getVariant = (el) => {
    if (el.classList.contains('cart__checkout-button')) return 'ink';
    if (el.classList.contains('sc-button')) return getColorVariant(el);
    if (el.classList.contains('sc-download-card__button')) return getColorVariant(el);
    if (el.classList.contains('sc-reseller-grid__view-more')) return 'dark';
    if (el.classList.contains('sc-video-card__play')) return 'light';
    if (el.classList.contains('sc-bundle-bar__button')) return 'orange';
    if (el.classList.contains('sc-drawer__cta')) return 'dark';
    if (el.classList.contains('sc-menu-card__btn--learn')) return 'light';
    if (el.classList.contains('sc-menu-card__btn--arrow')) return 'light';
    if (el.classList.contains('sc-menu-card__btn--outline')) return 'light';
    if (el.classList.contains('sc-menu-card__btn--solid')) return 'dark';
    if (el.classList.contains('sc-cfg__button--ghost')) return 'outline';
    if (el.classList.contains('button-secondary')) return 'outline';
    if (el.classList.contains('button--outline')) return 'light';
    if (el.classList.contains('partnership__cta') || el.classList.contains('header-actions__cta')) {
      return 'dark';
    }
    return 'primary';
  };

  const getPaddingBox = (el) => {
    const rect = el.getBoundingClientRect();
    const style = getComputedStyle(el);
    const borderLeft = parseFloat(style.borderLeftWidth) || 0;
    const borderTop = parseFloat(style.borderTopWidth) || 0;

    return {
      width: el.clientWidth,
      height: el.clientHeight,
      borderLeft,
      borderTop,
      rect,
    };
  };

  const positionBubble = (el, bubble, event) => {
    const { width, height, borderLeft, borderTop, rect } = getPaddingBox(el);
    const x = event.clientX - rect.left - borderLeft;
    const y = event.clientY - rect.top - borderTop;
    bubble.style.left = `${x}px`;
    bubble.style.top = `${y}px`;
    return { width, height, x, y };
  };

  const expandBubble = (el, bubble, event) => {
    const { width, height, x, y } = positionBubble(el, bubble, event);
    const maxDist = Math.hypot(Math.max(x, width - x), Math.max(y, height - y));
    // Extra scale covers 1px borders + subpixel anti-aliasing at rounded corners.
    const scale = (maxDist * 2) / BUBBLE_SIZE + 0.35;
    bubble.style.transform = `translate(-50%, -50%) scale(${scale})`;
  };

  const collapseBubble = (el, bubble, event) => {
    positionBubble(el, bubble, event);
    bubble.style.transform = 'translate(-50%, -50%) scale(0)';
  };

  const enhancedListeners = new WeakMap();

  const resetStaleEnhancement = (el) => {
    if (!enhancedListeners.has(el)) return;

    if (!el.querySelector('.bubble-button__bubble')) {
      const { onEnter, onLeave } = enhancedListeners.get(el);
      el.removeEventListener('mouseenter', onEnter);
      el.removeEventListener('mouseleave', onLeave);
      enhancedListeners.delete(el);
      delete el.dataset.bubbleEnhanced;
      el.classList.remove('bubble-button', ...VARIANT_CLASSES);
    }
  };

  const enhance = (el) => {
    resetStaleEnhancement(el);

    if (enhancedListeners.has(el) || el.disabled || el.getAttribute('aria-disabled') === 'true') {
      return;
    }
    el.dataset.bubbleEnhanced = 'true';

    el.classList.add('bubble-button', `bubble-button--${getVariant(el)}`);

    const label = document.createElement('span');
    label.className = 'bubble-button__label';
    while (el.firstChild) label.appendChild(el.firstChild);

    const bubble = document.createElement('span');
    bubble.className = 'bubble-button__bubble';
    bubble.setAttribute('aria-hidden', 'true');

    el.append(bubble, label);

    const onEnter = (event) => expandBubble(el, bubble, event);
    const onLeave = (event) => collapseBubble(el, bubble, event);
    el.addEventListener('mouseenter', onEnter);
    el.addEventListener('mouseleave', onLeave);
    enhancedListeners.set(el, { onEnter, onLeave });
  };

  const init = (root = document) => {
    root.querySelectorAll(DRAWER_CTA_SELECTOR).forEach(enhance);

    if (!hoverQuery.matches) return;

    root.querySelectorAll(SELECTOR).forEach((el) => {
      if (el.classList.contains('sc-drawer__cta')) return;
      enhance(el);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('bubble-button:init', (event) => init(event.detail?.root ?? document));
})();
